import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  FileText,
  FolderTree,
  Link2,
  Pin,
  Plus,
  Tag as TagIcon,
  Trash2,
  User as UserIcon,
  X,
  Zap,
} from 'lucide-react';
import type { ListPostsQuery, PostStatus } from '@news-cms/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import { PostListTable } from '@/components/posts/post-list-table';
import { hasPermission } from '@/lib/permissions';
import {
  postsListQueryOptions,
  useDeletePost,
  useDuplicatePost,
  usePurgePost,
  useRestorePost,
} from '@/lib/posts';
import { useSession } from '@/lib/auth';
import { usersListQueryOptions } from '@/lib/users';
import { categoriesListQueryOptions, tagsListQueryOptions } from '@/lib/taxonomies';
import { useQuery } from '@tanstack/react-query';

const STATUS_PILLS: { value: PostStatus; label: string }[] = [
  { value: 'draft', label: 'ฉบับร่าง' },
  { value: 'pending_review', label: 'รออนุมัติ' },
  { value: 'scheduled', label: 'ตั้งเวลา' },
  { value: 'published', label: 'เผยแพร่' },
  { value: 'archived', label: 'เก็บถาวร' },
];

const VALID_STATUSES = new Set<PostStatus>(STATUS_PILLS.map((p) => p.value));

interface PostsListSearch {
  page?: number;
  perPage?: number;
  status?: string; // comma-joined PostStatus list
  q?: string;
  authorId?: string;
  categoryId?: string;
  tagId?: string;
  sortBy?: 'updatedAt' | 'createdAt' | 'title' | 'publishedAt';
  sortDir?: 'asc' | 'desc';
  trash?: boolean;
  sticky?: boolean;
  breaking?: boolean;
}

function parseStatusParam(raw: string | undefined): PostStatus[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is PostStatus => VALID_STATUSES.has(s as PostStatus));
}

export const Route = createFileRoute('/_authenticated/posts/')({
  validateSearch: (search: Record<string, unknown>): PostsListSearch => ({
    page: typeof search.page === 'number' ? search.page : undefined,
    perPage: typeof search.perPage === 'number' ? search.perPage : undefined,
    status: typeof search.status === 'string' ? search.status : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    authorId: typeof search.authorId === 'string' ? search.authorId : undefined,
    categoryId: typeof search.categoryId === 'string' ? search.categoryId : undefined,
    tagId: typeof search.tagId === 'string' ? search.tagId : undefined,
    sortBy:
      search.sortBy === 'updatedAt' ||
      search.sortBy === 'createdAt' ||
      search.sortBy === 'title' ||
      search.sortBy === 'publishedAt'
        ? search.sortBy
        : undefined,
    sortDir: search.sortDir === 'asc' || search.sortDir === 'desc' ? search.sortDir : undefined,
    trash: search.trash === true ? true : undefined,
    sticky: search.sticky === true ? true : undefined,
    breaking: search.breaking === true ? true : undefined,
  }),
  component: PostsListPage,
});

function PostsListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const trashMode = search.trash === true;
  const selectedStatuses = parseStatusParam(search.status);
  const filter: Partial<ListPostsQuery> = {
    page: search.page ?? 1,
    perPage: search.perPage ?? 20,
    // Status filter is ignored server-side when trash=true, but we
    // pass it through for URL fidelity (toggling trash off restores it).
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    q: search.q,
    authorId: search.authorId,
    categoryId: search.categoryId,
    tagId: search.tagId,
    sortBy: search.sortBy ?? 'updatedAt',
    sortDir: search.sortDir ?? 'desc',
    trash: trashMode || undefined,
    sticky: search.sticky || undefined,
    breaking: search.breaking || undefined,
  };
  const query = useQuery(postsListQueryOptions(filter));
  const deleteMutation = useDeletePost();
  const restoreMutation = useRestorePost();
  const purgeMutation = usePurgePost();
  const duplicateMutation = useDuplicatePost();
  const [searchInput, setSearchInput] = useState(filter.q ?? '');

  const canCreate = hasPermission(user, 'post.create');
  // Backend gates GET /admin/users on user.update, so only roles that can
  // actually fetch the list get the author dropdown. Authors without
  // user.update can still filter via the URL ?authorId=<id> if they
  // know the id (or by clicking an author name elsewhere).
  const canListAuthors = hasPermission(user, 'user.update');
  const authorsQuery = useQuery({
    ...usersListQueryOptions({ perPage: 100 }),
    enabled: canListAuthors,
    staleTime: 60_000,
  });
  const authors = authorsQuery.data?.items ?? [];

  // Resolve category/tag names for the active filter chips. Only fires
  // when a filter is set so the typical "no filter" load skips them.
  // The list queries themselves are open to any authenticated admin —
  // permission-gated mutations only.
  const activeCategoryQuery = useQuery({
    ...categoriesListQueryOptions(),
    enabled: Boolean(search.categoryId),
    staleTime: 60_000,
  });
  const activeTagQuery = useQuery({
    ...tagsListQueryOptions(),
    enabled: Boolean(search.tagId),
    staleTime: 60_000,
  });
  const activeCategoryName = search.categoryId
    ? (activeCategoryQuery.data?.find((c) => c.id === search.categoryId)?.name ?? '...')
    : null;
  const activeTagName = search.tagId
    ? (activeTagQuery.data?.find((t) => t.id === search.tagId)?.name ?? '...')
    : null;
  // Author chip: prefer the cached authors list (only available with
  // user.update), then the session user's own name if it matches, then
  // a generic fallback. Editors filtering their own posts always get
  // "บทความของฉัน" regardless of user.update permission.
  let activeAuthorName: string | null = null;
  if (search.authorId) {
    if (user && search.authorId === user.id) {
      activeAuthorName = 'บทความของฉัน';
    } else {
      activeAuthorName = authors.find((a) => a.id === search.authorId)?.name ?? 'ผู้เขียนที่เลือก';
    }
  }
  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (filter.perPage ?? 20)));
  const currentPage = filter.page ?? 1;

  const toggleStatus = (value: PostStatus) => {
    const set = new Set(selectedStatuses);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    const next = Array.from(set);
    void navigate({
      search: {
        ...search,
        status: next.length > 0 ? next.join(',') : undefined,
        page: 1,
      },
    });
  };

  const clearStatus = () => {
    void navigate({ search: { ...search, status: undefined, page: 1 } });
  };

  const toggleTrashMode = () => {
    void navigate({
      search: { ...search, trash: trashMode ? undefined : true, page: 1 },
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({
      search: { ...search, q: searchInput.trim() || undefined, page: 1 },
    });
  };

  const goPage = (page: number) => {
    void navigate({ search: { ...search, page } });
  };

  // Single source of truth for "is anything filtered?" — used by both
  // the toolbar clear-all button and the smart empty-state pivot.
  // perPage isn't a filter (viewport preference) so it's preserved on
  // reset along with sortBy/sortDir.
  const hasActiveFilters = Boolean(
    search.q ||
    search.status ||
    search.authorId ||
    search.categoryId ||
    search.tagId ||
    search.sticky ||
    search.breaking ||
    trashMode,
  );
  const clearAllFilters = () =>
    void navigate({
      search: { page: 1, perPage: search.perPage, sortBy: search.sortBy, sortDir: search.sortDir },
    });

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              บทความข่าว
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {total > 0 ? `${total} บทความ` : 'ยังไม่มีบทความ'}
              {/* Active filter count — parity with /audit. Tells the
                  reader "this count is filtered" without scanning each
                  chip row. */}
              {(() => {
                const n = [
                  search.status,
                  search.q,
                  search.authorId,
                  search.categoryId,
                  search.tagId,
                  search.trash,
                  search.sticky,
                  search.breaking,
                ].filter(Boolean).length;
                return n > 0 ? (
                  <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    กรอง {n}
                  </span>
                ) : null;
              })()}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Share-link — parity with /audit toolbar (1d0fc90). Copy
                the full URL so editors can paste a filtered view (e.g.
                "pending_review by author X") into Slack/email. SPA
                route, works for anyone with post.read. */}
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => toast.success('คัดลอกลิงก์รวม filter แล้ว'))
                  .catch(() => {
                    // Clipboard denied — silent.
                  });
              }}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              title="คัดลอกลิงก์หน้านี้ (พร้อม filter)"
            >
              <Link2 className="h-3.5 w-3.5" />
              ลิงก์
            </button>
            {canCreate && (
              <Link to="/posts/new" className={buttonVariants({})}>
                <Plus className="mr-2 h-4 w-4" />
                เขียนข่าวใหม่
              </Link>
            )}
          </div>
        </div>

        {/* Status pills (multi-select) — hidden in trash mode since
            the server ignores status filter when trash=true */}
        {!trashMode && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={clearStatus}
              className={
                selectedStatuses.length === 0
                  ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                  : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
              }
            >
              ทั้งหมด
            </button>
            {STATUS_PILLS.map((p) => {
              const active = selectedStatuses.includes(p.value);
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => toggleStatus(p.value)}
                  className={
                    active
                      ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                      : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                  }
                  aria-pressed={active}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Flag + trash toggles — separated from the status pills so they
            read as orthogonal filters rather than just another status.
            Sticky/Breaking chips hidden in trash mode (don't make sense). */}
        <div className="flex flex-wrap items-center gap-2">
          {!trashMode && (
            <>
              <button
                type="button"
                onClick={() =>
                  void navigate({
                    search: { ...search, sticky: search.sticky ? undefined : true, page: 1 },
                  })
                }
                aria-pressed={search.sticky === true}
                className={
                  search.sticky
                    ? 'inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-xs font-medium text-white'
                    : 'inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                }
              >
                <Pin className="h-3 w-3" />
                ปักหมุด
              </button>
              <button
                type="button"
                onClick={() =>
                  void navigate({
                    search: { ...search, breaking: search.breaking ? undefined : true, page: 1 },
                  })
                }
                aria-pressed={search.breaking === true}
                className={
                  search.breaking
                    ? 'inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-medium text-white'
                    : 'inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                }
              >
                <Zap className="h-3 w-3" />
                ข่าวด่วน
              </button>
            </>
          )}
          <button
            type="button"
            onClick={toggleTrashMode}
            className={
              trashMode
                ? 'inline-flex items-center gap-1.5 rounded-full bg-slate-700 px-3 py-1 text-xs font-medium text-white'
                : 'inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }
            aria-pressed={trashMode}
          >
            <Trash2 className="h-3 w-3" />
            {trashMode ? 'กำลังดูถังขยะ — คลิกเพื่อกลับ' : 'ถังขยะ'}
          </button>
          {/* Author / category / tag filter chips — only appear when an
              id is active in the URL (entry points are the postCount
              links on /categories + /tags, and the clickable author
              name in each post row). Clickable X clears the filter.
              Name shows as "..." until the lookup query resolves. */}
          {activeAuthorName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              <UserIcon className="h-3 w-3" />
              {activeAuthorName}
              <button
                type="button"
                onClick={() =>
                  void navigate({ search: { ...search, authorId: undefined, page: 1 } })
                }
                aria-label="ล้างผู้เขียน"
                className="ml-1 rounded-full hover:bg-emerald-200 dark:hover:bg-emerald-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {activeCategoryName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <FolderTree className="h-3 w-3" />
              {activeCategoryName}
              <button
                type="button"
                onClick={() =>
                  void navigate({ search: { ...search, categoryId: undefined, page: 1 } })
                }
                aria-label="ล้างหมวด"
                className="ml-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {activeTagName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              <TagIcon className="h-3 w-3" />
              {activeTagName}
              <button
                type="button"
                onClick={() => void navigate({ search: { ...search, tagId: undefined, page: 1 } })}
                aria-label="ล้างแท็ก"
                className="ml-1 rounded-full hover:bg-purple-200 dark:hover:bg-purple-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {/* One-shot reset for when multiple filters stack up. Same
              predicate the smart empty-state uses, lifted to a shared
              const so the two surfaces can't drift. Preserves perPage
              + sortBy/sortDir since those are viewport prefs, not
              filters. */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
              title="ล้างทุกตัวกรอง (เก็บ sort + perPage)"
            >
              <X className="h-3 w-3" />
              ล้างทั้งหมด
            </button>
          )}
        </div>

        {/* Search + author filter */}
        <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ค้นหาหัวข้อ slug หรือเนื้อหา..."
            className="max-w-md"
          />
          <Button type="submit" variant="outline">
            ค้นหา
          </Button>
          {search.q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: { ...search, q: undefined, page: 1 } });
              }}
            >
              ล้าง
            </Button>
          )}
          {canListAuthors && (
            <select
              value={search.authorId ?? ''}
              onChange={(e) => {
                const nextId = e.target.value || undefined;
                void navigate({ search: { ...search, authorId: nextId, page: 1 } });
              }}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              aria-label="กรองตามผู้เขียน"
            >
              <option value="">ทุกผู้เขียน</option>
              {authors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
        </form>

        {/* Results */}
        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <FileText className="h-12 w-12 text-slate-300" />
            {hasActiveFilters ? (
              <>
                <p className="text-sm text-slate-500">ไม่พบบทความที่ตรงกับตัวกรอง</p>
                <Button type="button" variant="outline" size="sm" onClick={clearAllFilters}>
                  ล้างตัวกรองทั้งหมด
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500">ยังไม่มีบทความ</p>
                {canCreate && (
                  <Link to="/posts/new" className={buttonVariants({ size: 'sm' })}>
                    <Plus className="mr-2 h-4 w-4" />
                    เขียนข่าวแรก
                  </Link>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <PostListTable
              items={items}
              user={user}
              onDelete={(id) => deleteMutation.mutate(id)}
              isDeleting={deleteMutation.isPending}
              trashMode={trashMode}
              onRestore={(id) => restoreMutation.mutate(id)}
              isRestoring={restoreMutation.isPending}
              onPurge={(id) => purgeMutation.mutate(id)}
              isPurging={purgeMutation.isPending}
              sortBy={filter.sortBy}
              sortDir={filter.sortDir}
              onSortChange={(field) => {
                // Same field → flip direction; new field → start at desc.
                // "Most recent first" is the default reading order for both
                // dates and titles (alphabetical desc less common but fine).
                const nextDir: 'asc' | 'desc' =
                  filter.sortBy === field && filter.sortDir === 'desc' ? 'asc' : 'desc';
                void navigate({
                  search: { ...search, sortBy: field, sortDir: nextDir, page: 1 },
                });
              }}
              onDuplicate={(id) =>
                duplicateMutation.mutate(id, {
                  onSuccess: (post) => {
                    // Send the editor straight to the copy — that's the
                    // realistic next action (tweak headline, swap image,
                    // publish), and saves a second click through the list.
                    void navigate({ to: '/posts/$postId', params: { postId: post.id } });
                  },
                })
              }
              isDuplicating={duplicateMutation.isPending}
            />
            {/* Pagination row stays visible even on single-page results
                so the per-page selector is always reachable — switching
                from 20→100 is a common "show me more" gesture and
                hiding it would be a discoverability dead-end. */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">แสดง</span>
                <select
                  value={filter.perPage ?? 20}
                  onChange={(e) =>
                    void navigate({
                      search: { ...search, perPage: Number(e.target.value), page: 1 },
                    })
                  }
                  className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  aria-label="จำนวนรายการต่อหน้า"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-slate-500">รายการ</span>
              </div>
              {totalPages > 1 ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => goPage(currentPage - 1)}
                  >
                    ก่อนหน้า
                  </Button>
                  <span className="text-slate-500">
                    หน้า {currentPage} จาก {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => goPage(currentPage + 1)}
                  >
                    ถัดไป
                  </Button>
                </>
              ) : (
                <span className="text-slate-500">หน้า 1 จาก 1</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
